import React, { Component } from 'react';
import hubspotSubmit from 'hubspot-form-submit';
import PropTypes from 'prop-types';
import without from 'lodash/without';
import difference from 'lodash/difference';

import FormError from 'marketing-website/components/modules/FormError';
import FormErrorLink from 'marketing-website/components/modules/FormErrorLink';
import FormWrap from 'marketing-website/components/modules/FormWrap';

import {
  isCompanyNameValid,
  getCommenterParam,
  getIgnoreMarketingParam,
  getUsernameFromEmail,
  validateEmailAndPassword,
  makeCreateAccountRequest,
  splitNames,
} from 'marketing-website/utils/forms';

import { fetchApi } from 'marketing-website/utils/fetch';
import {
  API_COMPANY,
  API_CREATE_ACCOUNT,
  API_USER,
  HUBSPOT_ID,
} from 'marketing-website/constants/api';

export default class SignupForm extends Component {
  state = {
    backendErrorMsg: {},
    company: '',
    email: '',
    invalidFields: [],
    isPending: false,
    name: '',
    password: '',
    validFields: [],
  };

  fields = [
    {
      label: 'Full name',
      name: 'name',
      placeholder: "What's your full name?",
      class: 'input',
    },
    {
      errorMsg: 'Please enter a valid email address',
      label: 'Email address',
      name: 'email',
      placeholder: 'you@example.com',
      type: 'email',
      class: 'input',
    },
    {
      errorMsg: 'Must have at least 8 characters!',
      label: 'Password',
      name: 'password',
      placeholder: 'Enter 8 characters or more',
      type: 'password',
      class: 'input',
    },
    {
      errorMsg: 'Provided company name must be at least 2 characters!',
      label: 'Company name (optional)',
      name: 'company',
      placeholder: 'Where do you work?',
      class: 'input',
    },
  ];

  inputFieldNames = this.fields.map(field => field.name);

  setFieldValue = (e, fieldName) => {
    const value = e.target.value;
    let validFields = [];

    if (this.state.validFields.indexOf(fieldName) !== -1) {
      validFields = without(validFields, fieldName);
      this.setState({
        [fieldName]: value,
        validFields,
      });
    } else {
      this.setState({
        [fieldName]: value,
      });
    }
  };

  validateForm = e => {
    e.preventDefault();
    if (this.isFormValid()) {
      if (this.props.showFullForm) {
        this.createAccount();
      } else {
        this.updateUserDetails();
      }
    }
  };

  isFormValid = () => {
    const { email, password, company } = this.state;
    const invalidFields = [];

    if (this.props.showFullForm) {
      validateEmailAndPassword(email, password, invalidFields);
    }
    if (!isCompanyNameValid(company)) {
      invalidFields.push('company');
    }

    const validFields = difference(this.inputFieldNames, invalidFields);

    this.setState({
      invalidFields,
      backendErrorMsg: {},
      validFields,
    });
    return !invalidFields.length;
  };

  createAccount = () => {
    const { email, password, name, company } = this.state;
    this.setState({ isPending: true });
    makeCreateAccountRequest({
      email,
      password,
      name: name || getUsernameFromEmail(email),
      success: () => this.initialRequestSuccess(company),
      failure: this.displayServerError,
    });
  };

  updateUserDetails = () => {
    const { name, company } = this.state;
    this.setState({ isPending: true });
    const { userEmail } = this.props;
    if (name && userEmail) {
      fetchApi('PUT', API_USER, {
        username: name,
        email: userEmail,
      })
        .then(() => this.initialRequestSuccess(company))
        .catch(this.displayServerError);
    } else if (company) {
      this.updateCompany();
    } else {
      this.redirectTo();
    }
  };

  updateCompany = () => {
    fetchApi('PUT', API_COMPANY, {
      name: this.state.company,
      phone_number: '',
    })
      .then(this.redirectTo)
      .catch(this.displayServerError);
  };

  initialRequestSuccess = company => {
    const names = splitNames(this.state.name);
    const ignoreMarketing = getIgnoreMarketingParam();
    const isCommenter = getCommenterParam();
    this.secondaryRequestSuccess(company);

    hubspotSubmit(HUBSPOT_ID, this.props.hubspotId, {
      company,
      email: this.state.email,
      firstname: names.firstName,
      lastname: names.lastName,
      unsubscribed_from_emails: ignoreMarketing,
      commenter: isCommenter,
    });
  };

  secondaryRequestSuccess = company => {
    if (company) {
      this.updateCompany();
    } else {
      this.redirectTo();
    }
  };

  redirectTo = () => {
    window.location.href = this.props.redirectTo;
  };

  displayServerError = error => {
    const backendErrorMsg = getErrorMessage(error);

    this.setState({
      isPending: false,
      backendErrorMsg,
    });
  };

  render() {
    const {
      backendErrorMsg,
      invalidFields,
      isPending,
      validFields,
    } = this.state;

    const {
      showFullForm,
      messaging,
      buttonTxt,
      buttonColor,
      headerAlign,
      isCommenter,
    } = this.props;

    let inputsArray = showFullForm ? this.fields : this.fields.slice(0, 2);

    if (isCommenter) {
      inputsArray = this.fields.slice(0, 3);
    }

    const buttonProps = {
      text: buttonTxt,
      minWidth: 220,
      color: buttonColor,
      tabIndex: 9,
    };

    return (
      <FormWrap
        action={API_CREATE_ACCOUNT}
        autocomplete="off"
        backendErrorMsg={backendErrorMsg}
        buttonProps={buttonProps}
        fields={inputsArray}
        invalidFields={invalidFields}
        isPending={isPending}
        messaging={messaging}
        setFieldValue={this.setFieldValue}
        validateForm={this.validateForm}
        validFields={validFields}
        headerAlign={headerAlign}
        redirectTo={this.props.redirectTo}
      >
        <FormError {...backendErrorMsg} />
      </FormWrap>
    );
  }
}

SignupForm.propTypes = {
  buttonTxt: PropTypes.string.isRequired,
  redirectTo: PropTypes.string,
  messaging: PropTypes.object.isRequired,
  setShowFullForm: PropTypes.func,
  showFullForm: PropTypes.bool,
  userEmail: PropTypes.string,
  headerAlign: PropTypes.string,
  buttonColor: PropTypes.string,
  hubspotId: PropTypes.string,
  isCommenter: PropTypes.bool,
};

SignupForm.defaultProps = {
  showFullForm: true,
};

function getErrorMessage(error) {
  const data = (error && error.data) || {};

  if (data.email) {
    return {
      message: (
        <span>
          Email already exists.{' '}
          <FormErrorLink url="/signin/reset-password">
            Forgot password?
          </FormErrorLink>
        </span>
      ),
      fields: ['email'],
    };
  }

  if (data.invite_token) {
    return {
      message: (
        <span>
          This email is already associated to an account. Please accept the link
          in the email invite or{' '}
          <FormErrorLink url="https://help.marvelapp.com/" isExternal>
            contact support
          </FormErrorLink>
          .
        </span>
      ),
    };
  }

  if (data.password) {
    return {
      message: data.password,
      fields: ['password'],
    };
  }

  return {
    message: (
      <span>
        There was an error signing up. Please try again or{' '}
        <FormErrorLink url="https://help.marvelapp.com/" isExternal>
          contact support
        </FormErrorLink>
        .
      </span>
    ),
  };
}



// WEBPACK FOOTER //
// ./src/components/modules/SignupForm/SignupForm.js